// Typed wrappers for the Phase-8 /v1/platform/bank-accounts surface.
//
// Admin-only endpoints. payouts-backend gates with RequireRole(admin) — a
// non-admin caller gets 403 on every call here. The panel hides this surface
// from non-admin sidebars; calling it as a non-admin still surfaces a clean
// error rather than a UI hang.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance with Authorization + X-Merchant-ID injection. Platform-tier
// requests still carry X-Merchant-ID (the operator's current merchant context)
// but the backend ignores it; admin role takes precedence in middleware.
import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlatformProvider = "paysmart_idfc" | "buckzy_global_payout";
export type PlatformEnvironment = "sandbox" | "production";

export interface PlatformBankAccount {
  platform_bank_account_id: number;
  label: string;
  provider: PlatformProvider;
  environment: PlatformEnvironment;
  currency: string;
  status: string;
  created_at: string;
  disabled_at: string | null;
}

/**
 * CreatePlatformBankAccountPayload — body for POST /v1/platform/bank-accounts.
 *
 * The provider field decides which sub-fields are required:
 *   - paysmart_idfc        → email, password, merchant_key, base_url required.
 *   - buckzy_global_payout → no per-rail creds; the platform-Buckzy creds live
 *                            in a singleton row managed via createPlatformBuckzyCredential.
 */
export interface CreatePlatformBankAccountPayload {
  label: string;
  provider: PlatformProvider;
  environment: PlatformEnvironment;
  // IDFC-only:
  email?: string;
  password?: string;
  merchant_key?: string;
  base_url?: string;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

/**
 * createPlatformBankAccount — POST /v1/platform/bank-accounts.
 * Admin-only.
 */
export async function createPlatformBankAccount(
  payload: CreatePlatformBankAccountPayload,
): Promise<PlatformBankAccount> {
  const resp = await apiClient.post<{ platform_bank_account: PlatformBankAccount }>(
    `/v1/platform/bank-accounts`,
    payload,
  );
  return resp.data.platform_bank_account;
}

/**
 * listPlatformBankAccounts — GET /v1/platform/bank-accounts.
 * Admin-only. Returns newest-first.
 */
export async function listPlatformBankAccounts(): Promise<PlatformBankAccount[]> {
  const resp = await apiClient.get<{ platform_bank_accounts: PlatformBankAccount[] }>(
    `/v1/platform/bank-accounts`,
  );
  return resp.data.platform_bank_accounts;
}
